// Cart Discount Code Component
class CartDiscountCode extends HTMLElement {
  constructor() {
    super();
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleRemove = this.handleRemove.bind(this);
  }

  connectedCallback() {
    this.form = this.querySelector('form[data-discount-form]');
    this.input = this.querySelector('[data-discount-code-input]');
    this.button = this.querySelector('[data-discount-code-apply]');
    this.message = this.querySelector('[data-discount-code-message]');
    this.toggleButton = this.querySelector('[data-discount-code-toggle]');
    this.toggleButtonText = this.querySelector('[data-discount-code-toggle-text]');
    
    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit);
    }
    
    if (this.button && !this.form) {
      this.button.addEventListener('click', this.handleSubmit);
    }
    
    // Attach remove handlers to all remove buttons (in discount badges)
    this.attachRemoveHandlers();
    
    // Update toggle button text based on discount code
    if (this.input && this.input.value.trim()) {
      this.updateToggleButtonText(true);
      this.updateButtonText(true);
    }
    
    // Listen for accordion toggle to update button text
    const accordion = this.closest('.collapsible-tab');
    if (accordion) {
      const collapsibleButton = accordion.querySelector('collapsible-button');
      if (collapsibleButton) {
        collapsibleButton.addEventListener('click', () => {
          setTimeout(() => {
            this.updateToggleButtonText(this.input && this.input.value.trim());
          }, 100);
        });
      }
    }
    
    // For cart page: sync hidden field in main form whenever input changes
    if (window.location.pathname === '/cart') {
      this.setupCartPageSync();
    }
  }
  
  setupCartPageSync() {
    if (!this.input) return;
    
    const mainCartForm = document.querySelector('form[action*="/cart"]:not([data-discount-form])');
    if (!mainCartForm) {
      setTimeout(() => this.setupCartPageSync(), 200);
      return;
    }
    
    // Update hidden field whenever input changes
    const updateHiddenField = () => {
      const discountCode = this.input.value.trim();
      this.updateCartFormDiscount(discountCode);
    };
    
    this.input.addEventListener('input', updateHiddenField);
    this.input.addEventListener('change', updateHiddenField);
    this.input.addEventListener('blur', updateHiddenField);
    
    // Initialize hidden field on page load
    if (this.input.value.trim()) {
      updateHiddenField();
    }
    
    // Also check cart session on page load to ensure hidden field is correct
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        if (cart.discount_codes && cart.discount_codes.length > 0) {
          const cartDiscountCode = cart.discount_codes[0].code;
          // Update both input and hidden field from cart session
          if (this.input) {
            this.input.value = cartDiscountCode;
          }
          this.updateCartFormDiscount(cartDiscountCode);
        }
      })
      .catch(err => console.error('Error checking cart:', err));
    
    // Before form submits, ensure hidden field is updated
    mainCartForm.addEventListener('submit', (e) => {
      const submitter = e.submitter;
      if (submitter && (submitter.name === 'checkout' || submitter.classList.contains('cart--button-checkout'))) {
        // Get discount from input or cart session
        let discountCode = this.input ? this.input.value.trim() : '';
        
        // If no discount in input, check cart session
        if (!discountCode) {
          fetch('/cart.js')
            .then(response => response.json())
            .then(cart => {
              if (cart.discount_codes && cart.discount_codes.length > 0) {
                this.updateCartFormDiscount(cart.discount_codes[0].code);
              }
            })
            .catch(() => {});
        } else {
          this.updateCartFormDiscount(discountCode);
        }
      }
    }, true);
  }
  
  attachRemoveHandlers() {
    // Find all remove buttons in discount badges
    const removeButtons = document.querySelectorAll('[data-discount-code-remove]');
    removeButtons.forEach(button => {
      button.addEventListener('click', this.handleRemove);
    });
  }
  
  updateToggleButtonText(hasDiscount) {
    if (this.toggleButtonText) {
      this.toggleButtonText.textContent = hasDiscount ? 'Edit discount code' : 'Add discount code';
    }
  }

  disconnectedCallback() {
    if (this.form) {
      this.form.removeEventListener('submit', this.handleSubmit);
    }
    if (this.button) {
      this.button.removeEventListener('click', this.handleSubmit);
    }
    if (this.removeButton) {
      this.removeButton.removeEventListener('click', this.handleRemove);
    }
  }

  async handleSubmit(e) {
    e.preventDefault();
    e.stopPropagation();
    
    if (!this.input) return;
    
    const discountCode = this.input.value.trim();
    const isCartPage = window.location.pathname === '/cart';
    
    if (!discountCode) {
      this.showMessage('Please enter a discount code', 'error');
      return;
    }

    const originalButtonText = this.button ? this.button.textContent : 'Apply';
    if (this.button) {
      this.button.disabled = true;
      this.button.textContent = 'Applying...';
    }

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          discount: discountCode,
          sections: isCartPage ? '' : 'cart-drawer'
        })
      });

      const data = await response.json();

      if (!response.ok) {
        this.showMessage('Unable to apply discount code. Please try again.', 'error');
        if (this.button) {
          this.button.disabled = false;
          this.button.textContent = originalButtonText;
        }
        return;
      }

      // Verify discount was applied
      if (data.cart) {
        const hasDiscount = data.cart.discount_codes && data.cart.discount_codes.length > 0;
        const appliedCode = hasDiscount ? data.cart.discount_codes[0].code : null;
        
        if (!hasDiscount && discountCode) {
          this.showMessage('This discount code does not exist or could not be applied', 'error');
          if (this.button) {
            this.button.disabled = false;
            this.button.textContent = originalButtonText;
          }
          return;
        }
        
        // Ensure discount code is saved - update hidden field immediately
        if (appliedCode) {
          this.updateCartFormDiscount(appliedCode);
        }
      }

      // Success - update the UI
      if (isCartPage) {
        window.location.reload();
        return;
      }

      // Update cart drawer using eventBus
      if (window.eventBus && data.sections && data.sections['cart-drawer']) {
        window.eventBus.emit('update:cart:drawer', {
          sections: {
            'cart-drawer': data.sections['cart-drawer']
          }
        });
      } else if (data.sections && data.sections['cart-drawer']) {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          if (typeof cartDrawer.handleCartUpdate === 'function') {
            cartDrawer.handleCartUpdate({ 
              sections: { 
                'cart-drawer': data.sections['cart-drawer'] 
              } 
            });
          } else if (typeof cartDrawer.renderCartDrawer === 'function') {
            cartDrawer.renderCartDrawer(data.sections['cart-drawer']);
          }
        }
      } else {
        if (window.eventBus) {
          window.eventBus.emit('render:cart:drawer');
        }
      }
      
      // Update input value
      if (data.cart && data.cart.discount_codes && data.cart.discount_codes.length > 0) {
        this.input.value = data.cart.discount_codes[0].code;
        this.updateButtonText(true);
        this.updateToggleButtonText(true);
        this.showMessage('Discount code applied successfully', 'success');
      } else {
        this.updateButtonText(false);
        this.updateToggleButtonText(false);
        this.showMessage('Discount code applied successfully', 'success');
      }

      // Re-initialize after cart drawer updates
      setTimeout(() => {
        this.initializeElements();
        // Update hidden field again after cart drawer re-renders
        if (data.cart && data.cart.discount_codes && data.cart.discount_codes.length > 0) {
          this.updateCartFormDiscount(data.cart.discount_codes[0].code);
          this.updateButtonText(true);
          this.updateToggleButtonText(true);
        } else {
          this.updateButtonText(false);
          this.updateToggleButtonText(false);
        }
        // Re-attach remove handlers after cart drawer re-renders
        this.attachRemoveHandlers();
      }, 500);

    } catch (error) {
      console.error('Discount code error:', error);
      this.showMessage('An error occurred. Please try again.', 'error');
    } finally {
      if (this.button) {
        this.button.disabled = false;
        this.button.textContent = originalButtonText;
      }
    }
  }

  async handleRemove(e) {
    e.preventDefault();
    e.stopPropagation();
    
    if (!this.input) return;
    
    const isCartPage = window.location.pathname === '/cart';
    
    if (this.removeButton) {
      this.removeButton.disabled = true;
      this.removeButton.textContent = 'Removing...';
    }

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          discount: '',
          sections: isCartPage ? '' : 'cart-drawer'
        })
      });

      const data = await response.json();

      if (!response.ok) {
        this.showMessage('Unable to remove discount code. Please try again.', 'error');
        if (this.removeButton) {
          this.removeButton.disabled = false;
          this.removeButton.textContent = 'Remove';
        }
        return;
      }

      // Clear the input
      if (this.input) {
        this.input.value = '';
      }
      
      // Remove hidden field
      this.updateCartFormDiscount('');

      // Success - update the UI
      if (isCartPage) {
        window.location.reload();
        return;
      }

      // Update cart drawer
      if (window.eventBus && data.sections && data.sections['cart-drawer']) {
        window.eventBus.emit('update:cart:drawer', {
          sections: {
            'cart-drawer': data.sections['cart-drawer']
          }
        });
      } else if (data.sections && data.sections['cart-drawer']) {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          if (typeof cartDrawer.handleCartUpdate === 'function') {
            cartDrawer.handleCartUpdate({ 
              sections: { 
                'cart-drawer': data.sections['cart-drawer'] 
              } 
            });
          } else if (typeof cartDrawer.renderCartDrawer === 'function') {
            cartDrawer.renderCartDrawer(data.sections['cart-drawer']);
          }
        }
      } else {
        if (window.eventBus) {
          window.eventBus.emit('render:cart:drawer');
        }
      }
      
      this.updateButtonText(false);
      this.updateToggleButtonText(false);
      this.showMessage('Discount code removed', 'success');

      // Re-initialize after cart drawer updates
      setTimeout(() => {
        this.initializeElements();
        this.attachRemoveHandlers();
      }, 500);

    } catch (error) {
      console.error('Remove discount error:', error);
      this.showMessage('An error occurred. Please try again.', 'error');
    } finally {
      if (this.removeButton) {
        this.removeButton.disabled = false;
        this.removeButton.textContent = 'Remove';
      }
    }
  }

  initializeElements() {
    this.input = this.querySelector('[data-discount-code-input]');
    this.button = this.querySelector('[data-discount-code-apply]');
    this.message = this.querySelector('[data-discount-code-message]');
    this.toggleButton = this.querySelector('[data-discount-code-toggle]');
    this.toggleButtonText = this.querySelector('[data-discount-code-toggle-text]');
    
    // Show/hide button text based on input value
    if (this.input && this.input.value.trim()) {
      this.updateButtonText(true);
      this.updateToggleButtonText(true);
    } else {
      this.updateButtonText(false);
      this.updateToggleButtonText(false);
    }
  }

  updateButtonText(hasDiscount) {
    if (this.button) {
      if (hasDiscount) {
        this.button.textContent = 'Update';
      } else {
        const originalText = this.button.getAttribute('data-original-text') || 'Apply';
        this.button.textContent = originalText;
      }
    }
  }

  showMessage(text, type) {
    if (!this.message) return;
    this.message.textContent = text;
    this.message.className = `cart-discount-code--message cart-discount-code--message--${type}`;
    this.message.style.display = 'block';
    
    // Trigger height recalculation for accordion
    const accordion = this.closest('.collapsible-tab');
    if (accordion && accordion.classList.contains('open')) {
      const collapsibleButton = accordion.querySelector('collapsible-button');
      if (collapsibleButton && typeof collapsibleButton.resetHeight === 'function') {
        setTimeout(() => {
          collapsibleButton.resetHeight();
        }, 50);
      }
    }
    
    setTimeout(() => {
      if (this.message) {
        this.message.textContent = '';
        this.message.style.display = 'none';
        // Recalculate height when message is hidden
        if (accordion && accordion.classList.contains('open')) {
          const collapsibleButton = accordion.querySelector('collapsible-button');
          if (collapsibleButton && typeof collapsibleButton.resetHeight === 'function') {
            setTimeout(() => {
              collapsibleButton.resetHeight();
            }, 50);
          }
        }
      }
    }, 5000);
  }

  updateCartFormDiscount(discountCode) {
    // Update or create hidden discount field in cart drawer form
    const cartDrawerForm = document.getElementById('CartDrawer-Form');
    if (cartDrawerForm) {
      let hiddenDiscountField = cartDrawerForm.querySelector('input[name="discount"][type="hidden"]');
      
      if (discountCode) {
        if (hiddenDiscountField) {
          hiddenDiscountField.value = discountCode;
        } else {
          // Create hidden field if it doesn't exist
          hiddenDiscountField = document.createElement('input');
          hiddenDiscountField.type = 'hidden';
          hiddenDiscountField.name = 'discount';
          hiddenDiscountField.value = discountCode;
          cartDrawerForm.insertBefore(hiddenDiscountField, cartDrawerForm.firstChild);
        }
      } else {
        // Remove hidden field if discount code is empty
        if (hiddenDiscountField) {
          hiddenDiscountField.remove();
        }
      }
    }
    
    // Also update cart page form if on cart page
    if (window.location.pathname === '/cart') {
      const cartPageForm = document.querySelector('form[action*="/cart"]:not([data-discount-form])');
      if (cartPageForm) {
        let hiddenDiscountField = cartPageForm.querySelector('input[name="discount"][type="hidden"]');
        
        if (discountCode) {
          if (hiddenDiscountField) {
            hiddenDiscountField.value = discountCode;
          } else {
            hiddenDiscountField = document.createElement('input');
            hiddenDiscountField.type = 'hidden';
            hiddenDiscountField.name = 'discount';
            hiddenDiscountField.value = discountCode;
            cartPageForm.insertBefore(hiddenDiscountField, cartPageForm.firstChild);
          }
        } else {
          if (hiddenDiscountField) {
            hiddenDiscountField.remove();
          }
        }
      }
    }
  }
}

customElements.define('cart-discount-code', CartDiscountCode);
